import React, { useState } from 'react';
import ContactPopup from '../components/ContactPopup';
import { Building2, Handshake, Users2, BarChart3, AlertTriangle, FileCheck, HardHat, TrendingDown, Scale, Ruler, Calculator, Phone } from 'lucide-react';

const Partners: React.FC = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <>
    <main>
        {/* Hero Section */}
        <section className="relative h-[80vh] min-h-[600px] flex items-center overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center z-0"
            style={{ 
              backgroundImage: `url('https://images.pexels.com/photos/3183183/pexels-photo-3183183.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
              backgroundPosition: 'center center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 to-primary-900/50"></div>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 relative z-10 text-white animate-fade-in pt-32">
            <div className="max-w-3xl">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Working Together for Greater Impact
              </h1>
              <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl">
                At SL Rooms, partnership is more than a collaboration—it's a shared vision. Whether you're an investor, landowner, planning expert, or industry consultant, we're here to build value with you, brick by brick.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsContactOpen(true);
                  }}
                  className="px-6 py-3 bg-accent-400 hover:bg-accent-500 text-primary-950 font-medium rounded-md transition-colors duration-300 inline-flex items-center justify-center"
                >
                  Become a Partner
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Invest Section */}
        <section className="py-24 bg-white" id="benefits">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-primary-900">
                Why Partner With Us?
              </h2>
              <p className="text-gray-600">
                Join our network of successful property developers and investors to capitalize on the growing UK housing market while making a positive impact on communities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Market Analysis */}
              <div className="bg-white p-10 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                  <BarChart3 className="w-6 h-6 text-primary-700" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3 text-gray-900">
                  Strong Market Fundamentals
                </h3>
                <p className="text-gray-600">
                  The UK housing market shows robust growth with an 8.7% year-on-year rental increase and 4.6% house price appreciation in 2024, presenting compelling investment opportunities.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    Consistent market growth
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    High rental demand
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    Property value appreciation
                  </li>
                </ul>
              </div>

              {/* Strategic Conversion */}
              <div className="bg-white p-10 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                  <Building2 className="w-6 h-6 text-primary-700" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3 text-gray-900">
                  Strategic Conversion Advantage
                </h3>
                <p className="text-gray-600">
                  Our expertise in commercial-to-residential conversions offers streamlined planning processes and cost-effective solutions, maximising returns on investment.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    Optimised conversion costs
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    Faster planning approval
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-accent-400 rounded-full mr-2" />
                    Higher profit margins
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* Risks & Mitigation Strategies Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="bg-warning-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-warning-500" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-primary-900">
                Risks & Mitigation Strategies
              </h2>
              <p className="text-gray-600">
                We employ comprehensive risk management strategies to protect our investments and ensure project success.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Planning and Regulatory Risk */}
              <div className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="bg-warning-50 p-3 rounded-lg">
                    <FileCheck className="w-6 h-6 text-warning-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
                      Planning and Regulatory Risk
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-error-50 rounded-lg p-4">
                        <p className="text-error-700 font-medium">
                          Risk: Delays or refusals in obtaining planning permission could impact project timescales and costs.
                        </p>
                      </div>
                      <div className="bg-success-50 rounded-lg p-4">
                        <p className="text-success-700">
                          Mitigation: We engage experienced planning consultants and maintain strong relationships with local authorities.
                          Preliminary due diligence ensures compliance with regulations and identifies potential hurdles early in the process.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Construction and Cost Overruns */}
              <div className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="bg-warning-50 p-3 rounded-lg">
                    <HardHat className="w-6 h-6 text-warning-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
                      Construction and Cost Overruns
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-error-50 rounded-lg p-4">
                        <p className="text-error-700 font-medium">
                          Risk: Unexpected construction challenges or rising material costs could lead to budget overruns.
                        </p>
                      </div>
                      <div className="bg-success-50 rounded-lg p-4">
                        <p className="text-success-700">
                          Mitigation: We employ trusted contractors and Quantity Surveyors with proven track records and include contingency budgets in project planning.
                          Regular monitoring ensures prompt issue resolution.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Economic and Market Conditions */}
              <div className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="bg-warning-50 p-3 rounded-lg">
                    <TrendingDown className="w-6 h-6 text-warning-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
                      Economic and Market Conditions
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-error-50 rounded-lg p-4">
                        <p className="text-error-700 font-medium">
                          Risk: Economic downturns or inflation could impact property values and rental yields.
                        </p>
                      </div>
                      <div className="bg-success-50 rounded-lg p-4">
                        <p className="text-success-700">
                          Mitigation: We focus on resilient property sectors and maintain diverse portfolios. Projects are designed to appeal to high-demand tenant bases,
                          ensuring sustainability during economic uncertainty.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal and Structural Risks */}
              <div className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="bg-warning-50 p-3 rounded-lg">
                    <Scale className="w-6 h-6 text-warning-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold mb-4 text-gray-900">
                      Legal and Structural Risks
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-error-50 rounded-lg p-4">
                        <p className="text-error-700 font-medium">
                          Risk: Undiscovered legal issues or structural problems could result in unforeseen costs.
                        </p>
                      </div>
                      <div className="bg-success-50 rounded-lg p-4">
                        <p className="text-success-700">
                          Mitigation: We conduct thorough due diligence with legal experts, surveyors, and structural engineers before purchase to identify and address potential risks.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted Partners Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users2 className="w-8 h-8 text-primary-700" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-primary-900">
                Trusted Partners
              </h2>
              <p className="text-gray-600">
                Our strength lies in our diverse and highly qualified team of professionals, each
                bringing their unique expertise to the organisation. Here are the key power teams that
                drive our success:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
              <div className="text-center">
                <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HardHat className="w-8 h-8 text-primary-700" />
                </div>
                <h3 className="font-serif text-lg font-semibold mb-2 text-gray-900">Construction Experts</h3>
                <p className="text-sm text-gray-600">Leading contractors and project managers</p>
              </div>

              <div className="text-center">
                <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-primary-700" />
                </div>
                <h3 className="font-serif text-lg font-semibold mb-2 text-gray-900">Legal Advisors</h3>
                <p className="text-sm text-gray-600">Specialist property law firms</p>
              </div>

              <div className="text-center">
                <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ruler className="w-8 h-8 text-primary-700" />
                </div>
                <h3 className="font-serif text-lg font-semibold mb-2 text-gray-900">Architects</h3>
                <p className="text-sm text-gray-600">Innovative design specialists</p>
              </div>

              <div className="text-center">
                <div className="bg-primary-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-primary-700" />
                </div>
                <h3 className="font-serif text-lg font-semibold mb-2 text-gray-900">Financial Experts</h3>
                <p className="text-sm text-gray-600">Investment and funding specialists</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                <img src="https://storage.googleapis.com/msgsndr/Lqis7QfI2CPng4DTN4vN/media/6812046a4d0d3d44478d9dee.png" alt="Equa Academy" className="max-h-12 opacity-70 hover:opacity-100 transition-opacity" />
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                <img src="https://storage.googleapis.com/msgsndr/Lqis7QfI2CPng4DTN4vN/media/6812046a4d0d3d5d9f8d9ded.png" alt="Empowered Group" className="max-h-12 opacity-70 hover:opacity-100 transition-opacity" />
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                <img src="https://storage.googleapis.com/msgsndr/Lqis7QfI2CPng4DTN4vN/media/6812046aa00f640368f97677.png" alt="Nimbus" className="max-h-12 opacity-70 hover:opacity-100 transition-opacity" />
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                <img src="https://storage.googleapis.com/msgsndr/Lqis7QfI2CPng4DTN4vN/media/6812046a33fee4fb9b4984dc.png" alt="GMS" className="max-h-12 opacity-70 hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-24 bg-primary-600 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/3183183/pexels-photo-3183183.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center mix-blend-overlay"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6 text-white">
                Ready to Partner With Us?
              </h2>
              <p className="text-xl text-white/90 mb-12">
                Join us in transforming the UK's commercial spaces into thriving residential communities while 
                securing exceptional returns on your investment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsContactOpen(true);
                  }}
                  className="px-8 py-4 bg-white text-primary-600 font-medium rounded-md hover:bg-white/90 transition-colors duration-300 text-lg inline-flex items-center justify-center"
                >
                  Schedule a Consultation
                </a>
                <a
                  href="tel:+448085310127"
                  className="px-8 py-4 bg-primary-700 text-white font-medium rounded-md hover:bg-primary-800 transition-colors duration-300 text-lg inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  +44 808 531 0127
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    <ContactPopup isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default Partners